import React from 'react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList } from 'recharts'
import { useAtom } from 'jotai'
import type { ChartConfigIdType } from '@shared/constants.js'
import { themeAtom } from '../../atoms.js'
import { useChartData } from '../../hooks/useChartData.js'
import { sortBySecondProperty } from '../../utils.js'
import Card from '../Card.js'
import { getChartTheme } from './chartTheme.js'

type Props = {
  chartId: ChartConfigIdType
  title: string
}

const BAR_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
  '#f97316',
  '#6366f1',
  '#14b8a6',
  '#d946ef',
  '#0ea5e9',
  '#22c55e',
  '#eab308',
]

export default function PieChartCard({ chartId, title }: Props) {
  const [theme] = useAtom(themeAtom)
  const { data, loading, error } = useChartData(chartId)
  const t = getChartTheme(theme)

  if (loading) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-gray-400">loading...</div>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-red-400">加载失败</div>
      </Card>
    )
  }

  const rows = data.rows
  if (rows.length === 0) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-gray-400">数据为空</div>
      </Card>
    )
  }

  const dimensions = data.rowsMetadata.filter((m) => m.isDimension)
  const measures = data.rowsMetadata.filter((m) => !m.isDimension)
  const nameKey = dimensions[0]?.name ?? Object.keys(rows[0] ?? {})[0]!
  const valueKey = measures[0]?.name ?? Object.keys(rows[0] ?? {})[1]!

  const sortedRows = sortBySecondProperty(rows)
  const total = sortedRows.reduce((sum, row) => sum + Number(row[valueKey]), 0)
  const barData = sortedRows.map((row) => {
    const value = Number(row[valueKey])
    return {
      name: row[nameKey] as string,
      value,
      percent: total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '0%',
    }
  })

  // 每行留够高度，否则行数一多 recharts 会隔行隐藏 Y 轴标签
  const chartHeight = Math.max(200, barData.length * 30 + 40)

  return (
    <Card title={title} className="basis-full">
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={barData} layout="vertical" margin={{ top: 5, right: 50, bottom: 5, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.gridColor} horizontal={false} />
          <XAxis type="number" tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
          <YAxis
            type="category"
            dataKey="name"
            tick={t.axisTickProps}
            axisLine={t.axisLineProps}
            tickLine={t.tickLineProps}
            interval={0}
            width={95}
          />
          <Tooltip
            contentStyle={t.tooltipContentStyle}
            labelStyle={t.tooltipLabelStyle}
            itemStyle={t.tooltipItemStyle}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
            {barData.map((_entry, index) => (
              <Cell key={index} fill={BAR_COLORS[index % BAR_COLORS.length]} />
            ))}
            <LabelList dataKey="percent" position="right" style={{ fontSize: 12, fill: t.axisTickProps.fill }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  )
}
